import { postNamedQuery } from '../../../../../service'

// Build the Wealthbox-specific query params for `/v1/tasks`. Scope handling:
//  - `client` scope adds `resource_id=<contactId>` + `resource_type=Contact`.
//    These are the documented Wealthbox filter params (see the URL template at
//    https://dev.wealthbox.com under "Retrieve all tasks"). Wealthbox 400s on
//    `linked_to[id]` / `linked_to[type]` because `linked_to` is a body-shape
//    field, not a query param.
//  - `user` scope is translated server-side from the resolved token (the
//    backend listTasks operation injects `assigned_to=token.externalUserId`).
//  - `all` scope applies no automatic binding - just the explicit filters the
//    admin set. Useful for firm-wide totals composed by teamId / createdByScope
//    / etc.
// The caller can pass any other Wealthbox-supported filter through `extra`
// (e.g. `completed`, `task_type`, `updated_since`). Scope-bound `resource_id`
// / `resource_type` are applied AFTER `extra` so admin filters can't
// accidentally override the client-scope binding.
export function buildTaskParams ({ scope, contactId, extra = {} } = {}) {
  if (scope === 'user' || scope === 'all') {
    return { ...extra }
  }
  if (!contactId) return null
  return {
    ...extra,
    resource_id: contactId,
    resource_type: 'Contact'
  }
}

// True iff the scope requires a resolved Wealthbox contactId to be functional.
// Only the 'client' scope binds to a specific contact; 'user' and 'all' don't.
// Centralizing this so hooks/panels don't drift on which scopes need the link.
export function scopeRequiresClientLink (scope) {
  return scope === 'client'
}

// scopeFields is the bag of top-level fields the core-data `listTasks` query
// translates against the resolved token (currently `scope` and
// `createdByScope`). Spreading them at the request root - alongside `params` -
// matches the joi schema in `data-svc-core-data-api/.../schemas/tasks.js`.
function buildRequest ({ scope, scopeFields, baseParams, page }) {
  return {
    params: page == null ? baseParams : { ...baseParams, page },
    scope,
    ...(scopeFields || {})
  }
}

// Fetch the full paginated stream of tasks for the given scope/contactId. The
// metric uses the lighter `fetchTaskCount` below; the side panel walks pages
// and concatenates.
export async function fetchTasks ({ scope, contactId, params = {}, clientId, scopeFields, perPage = 25 }) {
  const baseParams = buildTaskParams({ scope, contactId, extra: { per_page: perPage, ...params } })
  if (!baseParams && scope === 'client') {
    return { tasks: [], totalCount: 0 }
  }

  const collected = []
  let totalCount = 0
  let page = 1

  while (true) {
    const { data } = await postNamedQuery('coreData', 'execute-wealthbox-domain-query', {
      query: 'listTasks',
      clientId,
      request: buildRequest({ scope, scopeFields, baseParams, page })
    })
    const items = data?.tasks ?? []
    collected.push(...items)
    totalCount = data?.meta?.total_count ?? collected.length

    const totalPages = data?.meta?.total_pages
    const currentPage = data?.meta?.current_page ?? page
    if (!totalPages || currentPage >= totalPages || items.length === 0) break
    page = currentPage + 1
    if (page > 40) break // hard safety cap on absurd pagination loops
  }

  return { tasks: collected, totalCount }
}

// Light count fetcher — reads `meta.total_count` from a single 1-row request
// so the metric tile doesn't pull the full task list to render `42`.
export async function fetchTaskCount ({ scope, contactId, params = {}, clientId, scopeFields }) {
  const baseParams = buildTaskParams({ scope, contactId, extra: { per_page: 1, ...params } })
  if (!baseParams && scope === 'client') return 0

  const { data } = await postNamedQuery('coreData', 'execute-wealthbox-domain-query', {
    query: 'listTasks',
    clientId,
    request: buildRequest({ scope, scopeFields, baseParams, page: null })
  })
  return data?.meta?.total_count ?? (data?.tasks?.length ?? 0)
}

// Generic Wealthbox categories fetcher. The server-side `listCategories`
// query maps `type` to the per-type Wealthbox endpoint
// (`/v1/categories/{type}_categories`) and unwraps the type-keyed response
// into a flat `categories` array. Returns Syd-Select-shaped options.
async function fetchWealthboxCategoriesByType (type, { clientId } = {}) {
  const { data } = await postNamedQuery('coreData', 'execute-wealthbox-domain-query', {
    query: 'listCategories',
    clientId,
    request: { params: { type } }
  })
  return (data?.categories ?? []).map(c => ({
    label: c.name,
    value: c.id
  }))
}

export async function fetchTaskCategories ({ clientId } = {}) {
  return fetchWealthboxCategoriesByType('task', { clientId })
}

export async function fetchEventCategories ({ clientId } = {}) {
  return fetchWealthboxCategoriesByType('event', { clientId })
}

// NOTE: Wealthbox notes do NOT have a category field. The original
// SMT-1353 ticket mentioned `category_id` on notes - that's wrong; notes
// use `tags` (string array) instead. fetchNoteCategories was deliberately
// removed.
